import { ThunkAction } from './common.actionTypes';
import { 
  CREATE_COURSE_REGISTRATION, 
  CreateCourseRegistrationAction,
  FETCH_ALL_USER_COURSES,
  FETCH_NEW_COURSES,
  FETCH_PROGRESS_USER_COURSES,
  FETCH_SUCCESS_USER_COURSES,
  FetchAllUserCoursesAction,
  FetchNewCoursesAction,
  FetchProgressUserCoursesAction,
  FetchSuccessUserCoursesAction,
  GET_CERTIFIED_COURSE_REGISTRATIONS,
  GetCertifiedCourseRegistrationsAction, 
  UPDATE_COURSE_REGISTRATION,
  UpdateCourseRegistrationAction
} from './course.actionTypes';
import courseService from '../services/course.service';
import services from '../services';
import {
  CourseRegistration,
  CourseRegistrationStatus,
  UserCoursesRequest
} from '@kimuno/api-sdk/lib/types/responses/CourseRegistration';
import { isCoursesPageLoading } from '../reducers/courses.selectors';
import { hasNext } from '../utils/pagination';
import { Pagination } from '../types/search';
import { ContentType, CourseMindItemSearchResult } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { SearchResponseData } from '@kimuno/api-sdk/lib/types/responses/Common';
import { SortByOption } from '@kimuno/api-sdk/lib/types/requests/Common';
import { CAROUSEL_DEFAULT_LENGTH } from '../constants';

export function createCourseRegistration(mindItemId: string): ThunkAction<CreateCourseRegistrationAction> {
  const payload = { mindItemId };
  return (dispatch) => {
    dispatch({ type: CREATE_COURSE_REGISTRATION.START, payload });
      
    return courseService.createCourseRegistration(mindItemId)
      .then(result => dispatch({ type: CREATE_COURSE_REGISTRATION.SUCCESS, payload, result }))
      .catch(error => dispatch({ error, type: CREATE_COURSE_REGISTRATION.ERROR, payload }));
  };
}

export function getCertifiedCourseRegistrations(): ThunkAction<GetCertifiedCourseRegistrationsAction> {
  return async (dispatch, getState) => {
    const me = getState().user.me;
    const allCertifiedCourseRegistrationsLoaded = getState().courses.certifiedCourseRegistrations.allLoaded;

    if (!me || allCertifiedCourseRegistrationsLoaded) { return; }

    const userId = me._id;
    const payload = { userId };

    dispatch({ type: GET_CERTIFIED_COURSE_REGISTRATIONS.START, payload });

    return courseService.getCertifiedCourseRegistrations(userId)
      .then(result => dispatch({ type: GET_CERTIFIED_COURSE_REGISTRATIONS.SUCCESS, result, payload }))
      .catch(error => dispatch({ error, type: GET_CERTIFIED_COURSE_REGISTRATIONS.ERROR, payload }));
  };
}

export function updateCourseRegistration(
  courseRegistration: CourseRegistration
): ThunkAction<UpdateCourseRegistrationAction> {
  return async (dispatch) => 
    dispatch({ type: UPDATE_COURSE_REGISTRATION.SUCCESS, result: courseRegistration });
}

export function fetchAllUserCourses(): ThunkAction<FetchAllUserCoursesAction> {
  return async (dispatch, getState) => {
    const me = getState().user.me;
    if (!me) { return; }

    const userId = me._id;
    const pagination = getState().courses.allCourses.pagination;

    if (isCoursesPageLoading(getState(), 'allCourses')) { return; }
    if (!hasNext(getState().courses.allCourses.pagination)) { return; }

    const request = {
      limit: pagination.limit,
      offset: pagination.offset
    };

    const payload = { pagination };

    dispatch({ type: FETCH_ALL_USER_COURSES.START, payload });
    return courseService.getUserCourses(userId, request)
      .then(result => dispatch({ type: FETCH_ALL_USER_COURSES.SUCCESS, result, payload }))
      .catch(error => dispatch({ error, type: FETCH_ALL_USER_COURSES.ERROR, payload }));
  };
}

export function fetchProgressUserCourses(): ThunkAction<FetchProgressUserCoursesAction> {
  return async (dispatch, getState) => {
    const me = getState().user.me;
    if (!me) { return; }

    const userId = me._id;
    const pagination = getState().courses.progressCourses.pagination;

    if (isCoursesPageLoading(getState(), 'progressCourses')) { return; }
    if (!hasNext(getState().courses.progressCourses.pagination)) { return; }

    const request: UserCoursesRequest = {
      limit: pagination.limit,
      offset: pagination.offset,
      registrationStatus: 'progress'
    };

    const payload: {
      pagination: Pagination, 
      registrationStatus: CourseRegistrationStatus
    } = { pagination, registrationStatus: 'progress' };

    dispatch({ type: FETCH_PROGRESS_USER_COURSES.START, payload });
    return courseService.getUserCourses(userId, request)
      .then(result => dispatch({ type: FETCH_PROGRESS_USER_COURSES.SUCCESS, result, payload }))
      .catch(error => dispatch({ error, type: FETCH_PROGRESS_USER_COURSES.ERROR, payload }));
  };
}

export function fetchSuccessUserCourses(): ThunkAction<FetchSuccessUserCoursesAction> {
  return async (dispatch, getState) => {
    const me = getState().user.me;
    if (!me) { return; }

    const userId = me._id;
    const pagination = getState().courses.successCourses.pagination;

    if (isCoursesPageLoading(getState(), 'successCourses')) { return; }
    if (!hasNext(getState().courses.successCourses.pagination)) { return; }

    const request: UserCoursesRequest = {
      limit: pagination.limit,
      offset: pagination.offset,
      registrationStatus: 'success'
    };

    const payload: {
      pagination: Pagination, 
      registrationStatus: CourseRegistrationStatus
    } = { pagination, registrationStatus: 'success' };

    dispatch({ type: FETCH_SUCCESS_USER_COURSES.START, payload });
    return courseService.getUserCourses(userId, request)
      .then(result => dispatch({ type: FETCH_SUCCESS_USER_COURSES.SUCCESS, result, payload }))
      .catch(error => dispatch({ error, type: FETCH_SUCCESS_USER_COURSES.ERROR, payload }));
  };
}

export function fetchNewCourses(
): ThunkAction<FetchNewCoursesAction> {
  return async (dispatch, getState) => {

    const newCourses = getState().dashboard.newCourses?.data;
    if (newCourses && newCourses.length > 0) { return; }

    const request = {
      offset: 0,
      limit: CAROUSEL_DEFAULT_LENGTH,
      contentType: ['course'] as ContentType[],
      sortBy: 'createdDate' as SortByOption,
    };

    dispatch({ type: FETCH_NEW_COURSES.START });
  
    return services.mindItem
      .search(request)
      .then((result) => {
        dispatch({ 
          type: FETCH_NEW_COURSES.SUCCESS,
          result: result as SearchResponseData<CourseMindItemSearchResult>,  
        });
      })
      .catch((error) => {
        dispatch({ error, type: FETCH_NEW_COURSES.ERROR });
      });
    
  };
}
